'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { Paragraph } from '../ui/paragraph';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { ProductCard } from '../product/product-card';
import { Button } from '../ui/button';
import Link from '@eliadmin/core/src/components/link';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';

export function Hero({
  title,
  description,
  image,
  products: initialProducts,
  locale,
}: {
  title: string;
  description: string;
  image: string;
  products: Product[];
  locale: string;
}) {
  const { me, isInitialized } = useCustomerStore();
  const { data: products = initialProducts } = useQuery({
    queryKey: [CacheKey.PRODUCTS, 'homepage-hero', locale, me?.customer.id ?? 'guest'],
    queryFn: () =>
      fetchProducts({
        locale,
        authToken: me?.authToken,
        query: { results: 5 },
      }).then(response => response.data),
    enabled: isInitialized,
    initialData: !me?.authToken ? initialProducts : undefined,
    placeholderData: initialProducts,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return (
    <section
      className={cn(
        'custom-section',
        'relative pb-12 pt-16 sm:pb-16 sm:pt-28 lg:pt-[145px] xl:pt-[300px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'relative z-10 flex flex-col items-start justify-start gap-12 sm:gap-16 md:flex-row md:items-end md:justify-between',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 md:max-w-[591px]">
          <h1 className="font-oswald text-4xl font-medium uppercase text-white sm:text-5xl lg:text-6xl xl:text-[64px]">
            {title}
          </h1>
          <Paragraph
            className="w-full font-medium"
            color="white"
            innerHtml={description}
            size="lg"
          />
        </div>
        <div className="relative z-40 h-[312px] w-full bg-white sm:h-[319px] md:max-w-[384px] lg:h-[333px]">
          <CarouselControls
            className={{
              main: 'absolute left-1/2 top-[55%] z-[80] flex w-[102%] -translate-x-1/2 -translate-y-1/2 items-center justify-between md:w-[110%]',
              button:
                'max-h-10 min-h-10 min-w-10 max-w-10 rounded-none border-none bg-primary shadow-none hover:bg-primary/90',
            }}
            nextIcon={<GoArrowRight size={20} className="text-white" />}
            prevIcon={<GoArrowLeft size={20} className="text-white" />}
          />
          <div
            className={cn(
              'absolute left-0 top-0 z-40 flex w-full flex-col items-start justify-start bg-white',
              'ea-hero__products-wrapper',
            )}
          >
            <Carousel
              enableLoop
              enableInterval
              intervalLength={3500}
              className={{
                main: 'relative z-40 w-full md:max-w-[384px]',
                wrapper: 'relative z-40 w-full md:max-w-[384px]',
                container: 'relative z-40 w-full md:max-w-[384px]',
                slide:
                  'relative z-20 flex w-full flex-col items-center justify-center gap-4 bg-white p-4 sm:gap-5 sm:p-5 lg:gap-6 lg:p-6',
              }}
              slides={products.map(product => (
                <ProductCard
                  product={product}
                  key={product.slug}
                  className={{
                    main: 'relative z-[400] pb-0',
                    image: 'aspect-auto h-[229px]',
                    name: 'line-clamp-1',
                  }}
                />
              ))}
            />
            <div className="w-full bg-white px-4 pb-4 sm:px-5 sm:pb-5 lg:px-6 lg:pb-6">
              <Button asChild className="h-14 w-full uppercase sm:h-[66px]">
                <Link prefetch={false} href={'/nase-produkty'}>
                  Naše produkty
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-hero__image-overlay')} />
      <Image
        src={image}
        alt={title}
        width={0}
        height={0}
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        sizes="100vw"
        loading="eager"
        priority
        fetchPriority="high"
      />
    </section>
  );
}
